import type { NodeStyle } from "./layout.types";
import { Display, Edge, Gutter } from "./yoga.types";
import type { YogaNode } from "./yoga.types";
import { defaultNodeLayoutStyle } from "../layoutEngine/layoutConstants";

/**
 * Applies the given style to the Yoga node.
 * @param style {Style} - The style to apply to the Yoga node
 * @param node {YogaNode} - The Yoga node to apply the style to
 */
export const applyYogaStyle = ({
  style,
  node
}: {
  style: Partial<NodeStyle>;
  node: YogaNode;
}) => {
  const fullStyle: NodeStyle = {
    ...defaultNodeLayoutStyle,
    ...style
  };

  node.setWidth(fullStyle.width);
  if (fullStyle.maxWidth !== undefined) {
    node.setMaxWidth(fullStyle.maxWidth);
  }

  if (fullStyle.minWidth !== undefined) {
    node.setMinWidth(fullStyle.minWidth);
  }

  node.setHeight(fullStyle.height);
  if (fullStyle.maxHeight !== undefined) {
    node.setMaxHeight(fullStyle.maxHeight);
  }

  if (fullStyle.minHeight !== undefined) {
    node.setMinHeight(fullStyle.minHeight);
  }

  node.setPadding(Edge.Top, fullStyle.paddingTop);
  node.setPadding(Edge.Bottom, fullStyle.paddingBottom);
  node.setPadding(Edge.Left, fullStyle.paddingLeft);
  node.setPadding(Edge.Right, fullStyle.paddingRight);

  node.setMargin(Edge.Top, fullStyle.marginTop);
  node.setMargin(Edge.Bottom, fullStyle.marginBottom);
  node.setMargin(Edge.Left, fullStyle.marginLeft);
  node.setMargin(Edge.Right, fullStyle.marginRight);

  // node.setAlignContent(fullStyle.alignContent);
  node.setAlignItems(fullStyle.alignItems);
  node.setAlignSelf(fullStyle.alignSelf);

  node.setJustifyContent(fullStyle.justifyContent);

  node.setFlexBasis(fullStyle.flexBasis);
  node.setFlexGrow(fullStyle.flexGrow);
  node.setFlexShrink(fullStyle.flexShrink);

  node.setFlexWrap(fullStyle.flexWrap);
  node.setFlexDirection(fullStyle.flexDirection);

  node.setGap(Gutter.Row, fullStyle.gapRow);
  node.setGap(Gutter.Column, fullStyle.gapColumn);

  node.setOverflow(fullStyle.overflow);
  node.setPositionType(fullStyle.position);

  if (fullStyle.left !== undefined) {
    node.setPosition(Edge.Left, fullStyle.left);
  }

  if (fullStyle.top !== undefined) {
    node.setPosition(Edge.Top, fullStyle.top);
  }

  if (fullStyle.right !== undefined) {
    node.setPosition(Edge.Right, fullStyle.right);
  }

  if (fullStyle.bottom !== undefined) {
    node.setPosition(Edge.Bottom, fullStyle.bottom);
  }

  node.setDisplay(fullStyle.visible ? Display.Flex : Display.None);
  if (fullStyle.aspectRatio !== undefined) {
    node.setAspectRatio(fullStyle.aspectRatio);
  }
  if (fullStyle.borderWidth !== undefined) {
    node.setBorder(Edge.All, fullStyle.borderWidth);
  }
};
